import React from "react";
import { MapPin, Monitor, CreditCard, ShieldCheck, Clock, Calendar, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "../context/ContentContext";
import { mediaUrl } from "../lib/api";
import { external } from "../lib/links";

export const Booking = () => {
  const { content } = useContent();
  const b = content.booking;
  const links = content.links;

  return (
    <section id="booking" data-testid="booking-section" className="relative py-20 md:py-28 bg-cream">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <Reveal><p className="text-xs md:text-sm uppercase tracking-[0.24em] font-semibold text-ocean">{b.eyebrow}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">{b.heading}</h2>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-8 flex items-end gap-3">
                <span className="font-serif text-6xl md:text-7xl text-sage-dark leading-none">{b.rate}</span>
                <span className="text-ink-soft text-lg pb-2">/ {b.session}</span>
              </div>
              <p className="mt-3 text-ink-muted text-sm max-w-md">{b.note}</p>
            </Reveal>

            <div className="mt-10 grid sm:grid-cols-2 gap-5">
              {[b.inPerson, b.online].map((opt, i) => (
                <Reveal key={opt.title} delay={0.12 + i * 0.05}>
                  <div className="rounded-2xl border border-cream-2 bg-white p-6 h-full">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-peach/20 text-ink">
                      {i === 0 ? <MapPin className="h-5 w-5" /> : <Monitor className="h-5 w-5" />}
                    </span>
                    <h3 className="mt-4 font-serif text-2xl text-ink">{opt.title}</h3>
                    <p className="mt-2 text-sm font-medium text-sage-dark">{opt.when}</p>
                    <p className="text-sm text-ink-soft">{opt.where}</p>
                  </div>
                </Reveal>
              ))}
            </div>

            <div className="mt-8 space-y-4">
              <Reveal delay={0.16}>
                <div className="flex items-start gap-3">
                  <CreditCard className="h-5 w-5 text-sage-dark mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-soft"><span className="font-medium text-ink">Payment: </span>{(b.payments || []).join(" · ")}</p>
                </div>
              </Reveal>
              <Reveal delay={0.19}>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-sage-dark mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-soft">
                    <span className="font-medium text-ink">Insurance: </span>{b.insurance}
                    <span className="block mt-1 text-ink-muted">{b.certification}</span>
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.22}>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-sage-dark mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-soft"><span className="font-medium text-ink">Cancellation: </span>{b.cancellation}</p>
                </div>
              </Reveal>
              <Reveal delay={0.25}>
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-sage-dark mt-0.5 shrink-0" />
                  <p className="text-sm text-ink-soft"><span className="font-medium text-ink">Online sessions: </span>{b.onlineSessions}</p>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="lg:sticky lg:top-28">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_60px_-24px_rgba(122,143,119,0.4)]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={mediaUrl(b.image)} alt="A warm, inviting counselling space" className="h-full w-full object-cover" />
                </div>
                <div className="bg-white p-8">
                  <h3 className="font-serif text-3xl text-ink">{b.cardHeading}</h3>
                  <p className="mt-3 text-ink-soft leading-relaxed">{b.cardBody}</p>
                  <a data-testid="booking-book-btn" href={links.janeBook} {...external} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage text-white px-8 py-4 font-semibold transition-[background-color,transform,box-shadow] duration-300 hover:bg-sage-dark hover:-translate-y-0.5 hover:shadow-xl">
                    {b.cardCta} <ArrowUpRight className="h-5 w-5" />
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Booking;
